import React, { useEffect, useState } from "react";
import HeaderFilter from "./property-report/HeaderFilter";
import { defaultValuesMap, schemaMap } from "../schema/FilterSchema";
import SkeletonTable from "./property-report/SkeletonTable";
import { formatPrice } from "../../../shared/utils/formatPrice";
import { toast } from "sonner";

// V4 Imports
import useReportAgentV4 from "../../../services/cm-report/hooks/useReportAgentV4";
import ReportAgentTableV4 from "./property-report/ReportAgentTableV4";
import useReportRoomV4 from "../../../services/cm-report/hooks/useReportRoomV4";
import RoomReportTableV4 from "./property-report/RoomReportTableV4";
import useDailyReportV4 from "../../../services/cm-report/hooks/useDailyReportV4";
import DailyReportTableV4 from "./property-report/DailyReportTableV4";
import useSalesReportV4 from "../../../services/cm-report/hooks/useSalesReportV4";
import SalesReportTableV4 from "./property-report/SalesReportTableV4";
import useCountryReportV4 from "../../../services/cm-report/hooks/useCountryReportV4";
import CountryReportV4 from "./property-report/CountryReportV4";
import useReportSalesMonthlyV4 from "../../../services/cm-report/hooks/useReportSalesMonthlyV4";
import ReportSalesMonthlyTableV4 from "./property-report/ReportSalesMonthlyTableV4";
// import ReportSalesMonthlyChartV4 from "./property-report/ReportSalesMonthlyChartV4";
import useReportReservationV4 from "../../../services/cm-report/hooks/useReportReservationV4";
import ReportReservationTableV4 from "./property-report/ReportReservationTableV4";
// import ReportReservationChartV4 from "./property-report/ReportReservationChartV4";
import AgentHighChart3D from "./property-report/AgentHighChart3D";
import RoomHighChart3D from "./property-report/RoomHighChart3D";
import ReportSalesMonthlyHighChart from "./property-report/ReportSalesMonthlyHighChart";
import ReportReservationHighChart from "./property-report/ReportReservationHighChart";


type ReportType = keyof typeof schemaMap;
type SortDirection = "asc" | "desc";
type AgentOrderByOption = "agent_name" | "percentage" | "room_nights" | "reservations" | "avg_nights" | "booking_window" | "revenue" | "ota_com" | "avg_rate" | "cancel_count";
type DailyOrderByOption = "date" | "rooms" | "room_sold" | "daily_revenue" | "revpar" | "occupancy" | "available";
type SalesOrderByOption = "property_name" | "booking_date" | "source" | "reservation_number" | "guest_name" | "checkin_date" | "checkout_date" | "extra" | "amount";
type CountryOrderByOption = "country" | "nb_reservations" | "amount";
type RoomOrderByOption = "room_type" | "percentage" | "room_nights" | "reservations" | "avg_nights" | "booking_window" | "revenue" | "ota_com" | "avg_rate" | "cancel_count";

// Define the specific filter types for V4 hooks
interface ReportFilterV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "stay_date" | "created_reservation";
    room_type_id?: string;
    // Only consumed by the daily_report endpoint (useDailyReportV4) — harmless no-op
    // for the other report types that share this filter shape.
    ota_code?: string;
    agent_id?: string;
}

interface CountryReportFilterV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "checkin_date" | "created_reservation";
    room_type_id?: string;
}

interface ReportSalesMonthlyFilterV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "created_reservation";
    room_type_id?: string;
}

interface ReportReservationFilterV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "created_reservation" | "checkin_date" | "checkout_date";
    room_type_id?: string;
}

const PropertyContent: React.FC = () => {
    const [reportType, setReportType] = useState<ReportType>("report_agent");
    const [filters, setFilters] = useState(() => defaultValuesMap["report_agent"]);

    // State untuk sorting dibuat lebih generik
    const [sortConfig, setSortConfig] = useState<{ orderBy: string; sort: SortDirection }>({
        orderBy: "agent_name",
        sort: "desc",
    });

    // Reset sorting saat reportType berubah
    useEffect(() => {
        if (reportType === 'report_agent') {
            setSortConfig({ orderBy: 'agent_name', sort: 'desc' });
        } else if (reportType === 'daily_report') {
            setSortConfig({ orderBy: 'date', sort: 'asc' });
        } else if (reportType === 'sales_report') {
            setSortConfig({ orderBy: 'booking_date', sort: 'asc' });
        } else if (reportType === 'country_report') {
            setSortConfig({ orderBy: 'country', sort: 'asc' });
        } else if (reportType === 'room_report') {
            setSortConfig({ orderBy: 'room_type', sort: 'desc' });
        }
    }, [reportType]);

    // Handler untuk mengubah sorting
    const handleSort = (newOrderBy: string) => {
        setSortConfig(prevConfig => ({
            orderBy: newOrderBy,
            sort: prevConfig.orderBy === newOrderBy && prevConfig.sort === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Fetch data based on `reportType`
    // const { data: countryData, isFetching: isCountryFetching } = useCountryReport({
    //     filter: filters.filter,
    //     type: reportType
    // });

    const { data: countryData, isFetching: isCountryFetching } = useCountryReportV4({
        filter: filters.filter as CountryReportFilterV4, // Sesuaikan tipe jika perlu
        orderBy: sortConfig.orderBy as CountryOrderByOption, // Gunakan type assertion
        sort: sortConfig.sort,
        type: reportType
    });

    // const { data: dailyData, isFetching: isDailyFetching } = useDailyReport({
    //     filter: filters.filter,
    //     type: reportType
    // });

    const { data: dailyData, isFetching: isDailyFetching } = useDailyReportV4({
        filter: filters.filter as ReportFilterV4, // Ensure type compatibility
        orderBy: sortConfig.orderBy as DailyOrderByOption, // Gunakan type assertion
        sort: sortConfig.sort,
        type: reportType
    });

    // const { data: salesData, isFetching: isSalesFetching } = useSalesReport({
    //     filter: filters.filter,
    //     type: reportType
    // });

    const { data: salesData, isFetching: isSalesFetching } = useSalesReportV4({
        filter: filters.filter as ReportFilterV4, // Sesuaikan tipe jika perlu
        orderBy: sortConfig.orderBy as SalesOrderByOption, // Gunakan type assertion
        sort: sortConfig.sort,
        type: reportType
    });

    // const { data: reportSalesMonthlyData, isFetching: isReportSalesMonthlyFetching } = useReportSalesMonthly({
    //     filter: filters.filter,
    //     type: reportType
    // });

    const { data: reportSalesMonthlyData, isFetching: isReportSalesMonthlyFetching } = useReportSalesMonthlyV4({
        filter: {
            property_id: filters.filter.property_id,
            base_on: filters.filter.base_on as "created_reservation",
            room_type_id: filters.filter.room_type_id,
        } as ReportSalesMonthlyFilterV4,
        type: reportType
    });

    // const { data: reportReservationData, isFetching: isReportReservationFetching } = useReportReservation({
    //     filter: filters.filter,
    //     type: reportType
    // });

    const { data: reportReservationData, isFetching: isReportReservationFetching } = useReportReservationV4({
        filter: {
            property_id: filters.filter.property_id,
            base_on: filters.filter.base_on as "created_reservation",
            room_type_id: filters.filter.room_type_id,
        } as ReportReservationFilterV4,
        type: reportType
    });

    const { data: reportAgentData, isFetching: isReportAgentFetching } = useReportAgentV4({
        filter: filters.filter as ReportFilterV4,
        orderBy: sortConfig.orderBy as AgentOrderByOption,
        sort: sortConfig.sort,
        type: reportType
    });

    // UPDATED: Use the V4 hook for room reports
    const { data: reportRoomData, isFetching: isRoomFetching } = useReportRoomV4({
        filter: filters.filter as ReportFilterV4,
        orderBy: sortConfig.orderBy as RoomOrderByOption,
        sort: sortConfig.sort,
        type: reportType
    });

    // Toast notifikasi saat data berhasil di-load sesuai reportType aktif
    useEffect(() => {
        const map: Record<string, { fetching: boolean; data: any }> = {
            report_agent: { fetching: isReportAgentFetching, data: reportAgentData },
            room_report: { fetching: isRoomFetching, data: reportRoomData },
            daily_report: { fetching: isDailyFetching, data: dailyData },
            sales_report: { fetching: isSalesFetching, data: salesData },
            country_report: { fetching: isCountryFetching, data: countryData },
            report_sales_monthly: { fetching: isReportSalesMonthlyFetching, data: reportSalesMonthlyData },
            report_reservation: { fetching: isReportReservationFetching, data: reportReservationData },
        };
        const current = map[reportType];
        if (current && !current.fetching && current.data) {
            toast.success('Property report data loaded successfully.');
        }
    }, [
        reportType,
        isReportAgentFetching, reportAgentData,
        isRoomFetching, reportRoomData,
        isDailyFetching, dailyData,
        isSalesFetching, salesData,
        isCountryFetching, countryData,
        isReportSalesMonthlyFetching, reportSalesMonthlyData,
        isReportReservationFetching, reportReservationData,
    ]);

    const isAnyFetching = isCountryFetching || isDailyFetching || isSalesFetching || isReportSalesMonthlyFetching || isReportReservationFetching || isReportAgentFetching || isRoomFetching;

    return (
        <div className="container-fluid mt-4">
            <HeaderFilter setReportType={setReportType} setFilters={setFilters} isLoading={isAnyFetching} />

            <h3 className="mt-4">
                {reportType === "country_report" ? "Country Report" :
                    reportType === "daily_report" ? "Daily Report" :
                        reportType === "sales_report" ? "Sales Report" :
                            reportType === "report_sales_monthly" ? "Reservation Last 12 Months Report" :
                                reportType === "report_reservation" ? "Sales Last 12 Months Report" :
                                    reportType === "report_agent" ? "Agent Report" :
                                        reportType === "room_report" ? "Room Report" : "Unknown Report"}
            </h3>

            {isCountryFetching && reportType === "country_report" ? (
                <SkeletonTable rows={10} columns={3} />
            ) : isDailyFetching && reportType === "daily_report" ? (
                <SkeletonTable rows={10} columns={7} />
            ) : isSalesFetching && reportType === "sales_report" ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isReportSalesMonthlyFetching && reportType === 'report_sales_monthly' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isReportReservationFetching && reportType === 'report_reservation' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isReportAgentFetching && reportType === 'report_agent' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : isRoomFetching && reportType === 'room_report' ? (
                <SkeletonTable rows={10} columns={8} />
            ) : (
                <>
                    {/* {reportType === "country_report" && countryData && "data" in countryData && (
                        <CountryReport data={countryData} />
                    )} */}
                    {reportType === "country_report" && countryData && "data" in countryData && (
                        <CountryReportV4
                            data={countryData}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                        />
                    )}
                    {/* {reportType === "daily_report" && dailyData && "data" in dailyData && (
                        <DailyReportTable data={dailyData} />
                    )} */}
                    {reportType === "daily_report" && dailyData && "data" in dailyData && (
                        <DailyReportTableV4
                            data={dailyData}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                        />
                    )}
                    {/* {reportType === "sales_report" && salesData && "data" in salesData && (
                        <SalesReportTable data={salesData} />
                    )} */}
                    {reportType === "sales_report" && salesData && "data" in salesData && (
                        <SalesReportTableV4
                            data={salesData}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                        />
                    )}
                    {/* {reportType === 'report_sales_monthly' && reportSalesMonthlyData && 'data' in reportSalesMonthlyData && (
                        <>
                            <ReportSalesMonthlyTable data={reportSalesMonthlyData} startDate={filters.filter.start_date} endDate={filters.filter.end_date} />
                            <div className="mt-4">
                                <h3>Sales Monthly Chart</h3>
                                <ReportSalesMonthlyChart data={reportSalesMonthlyData.data} />
                            </div>
                        </>
                    )} */}

                    {reportType === 'report_sales_monthly' && reportSalesMonthlyData && 'data' in reportSalesMonthlyData && (
                        <>
                            <ReportSalesMonthlyTableV4 data={reportSalesMonthlyData} />
                            <div className="mt-4">
                                <h3>Reservation Last 12 Months Chart</h3>
                                {/* <ReportSalesMonthlyChartV4 data={reportSalesMonthlyData} /> */}
                                <ReportSalesMonthlyHighChart data={reportSalesMonthlyData} />
                            </div>
                        </>
                    )}

                    {/* {reportType === 'report_reservation' && reportReservationData && 'data' in reportReservationData && (
                        <>
                            <ReportReservationTable data={reportReservationData} startDate={filters.filter.start_date} endDate={filters.filter.end_date} />
                            <div className="mt-4">
                                <h3>Reservation Chart</h3>
                                <ReportReservationChart data={reportReservationData.data} />
                            </div>
                        </>
                    )} */}


                    {reportType === 'report_reservation' && reportReservationData && 'data' in reportReservationData && (
                        <>
                            <ReportReservationTableV4 data={reportReservationData} />
                            <div className="mt-4">
                                <h3>Sales Last 12 Months Chart</h3>
                                {/* <ReportReservationChartV4 data={reportReservationData} /> */}
                                <ReportReservationHighChart data={reportReservationData} />
                            </div>
                        </>
                    )}

                    {reportType === 'report_agent' && reportAgentData && 'data' in reportAgentData && (
                        <>
                            <ReportAgentTableV4
                                data={reportAgentData}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            <div className="mt-4">
                                <h3>Agent Chart</h3>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <AgentHighChart3D
                                            title="Number Of Room Sold By Source Of: Agent"
                                            data={reportAgentData.data}
                                            dataKey="room_nights"
                                        />
                                    </div>
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <AgentHighChart3D
                                            title="Total Revenue By Source Of: Agent"
                                            data={reportAgentData.data}
                                            dataKey="revenue"
                                            valueFormatter={formatPrice}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {/* UPDATED: Render V4 components for room_report */}
                    {reportType === 'room_report' && reportRoomData && 'data' in reportRoomData && (
                        <>
                            <RoomReportTableV4
                                data={reportRoomData}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            <div className="mt-4">
                                <h3>Room Chart</h3>
                                <div className="d-flex flex-wrap justify-content-center gap-3">
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <RoomHighChart3D
                                            title="Number Of Room Sold By Source Of: Rooms"
                                            data={reportRoomData.data}
                                            dataKey="room_nights"
                                        />
                                    </div>
                                    <div className="flex-grow-1 responsive-chart-container">
                                        <RoomHighChart3D
                                            title="Total Revenue By Source Of: Rooms"
                                            data={reportRoomData.data}
                                            dataKey="revenue"
                                            valueFormatter={formatPrice}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PropertyContent;